import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, Users, Globe, Building, UserPlus, RefreshCw } from 'lucide-react';
import { CommunityMapTable } from '@/components/community/CommunityMapTable';
import { CommunityMemberFormDialog } from '@/components/community/CommunityMemberFormDialog';
import { useCommunityMembers, useDeleteCommunityMember, useUpdateCommunityMember, useCreateCommunityMember, useSyncCommunityMap } from '@/hooks/useCommunityMap';
import type { CommunityMember, CommunityMemberFilters } from '../../../../shared/types';
import { downloadXlsx } from '@/lib/xlsx';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function CommunityMapPage() {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedMember, setSelectedMember] = useState<CommunityMember | null>(null);
  const [filters, setFilters] = useState<CommunityMemberFilters>({ status: ['Activo'] });

  const { data: members = [], isLoading } = useCommunityMembers(filters);
  const deleteMember = useDeleteCommunityMember();
  const updateMember = useUpdateCommunityMember();
  const createMember = useCreateCommunityMember();
  const syncFromSheets = useSyncCommunityMap();

  const handleCreate = () => {
    setFormMode('create');
    setSelectedMember(null);
    setIsFormOpen(true);
  };

  const handleEdit = (member: CommunityMember) => {
    setFormMode('edit');
    setSelectedMember(member);
    setIsFormOpen(true);
  };

  const handleSave = async (memberId: string | null, data: Partial<CommunityMember>) => {
    if (formMode === 'create') {
      await createMember.mutateAsync(data as any);
      toast.success(t('users.toasts.member_created'));
    } else if (memberId) {
      await updateMember.mutateAsync({ memberId, updates: data });
      toast.success(t('users.toasts.member_updated'));
    }
  };

  const handleDelete = async (memberId: string) => {
    if (window.confirm(t('users.community_map_page.delete_confirm'))) {
      await deleteMember.mutateAsync(memberId);
    }
  };

  const handleExportToExcel = async () => {
    try {
      // Preparar datos para Excel
      const excelData = members.map((member) => ({
        ID: member.employeeId,
        Name: member.name,
        'Last name': member.lastName,
        Mail: member.email,
        Country: member.country,
        Clapbase: member.clapbase,
        Role: member.role,
        Level: member.level || '-',
        Team: member.team || '-',
        Area: member.area || '-',
        'People Manager': member.peopleManager || '-',
        'N+1 Manager': member.n1Manager || '-',
        'Responsable area': member.responsableArea || '-',
        'Start date': member.startDate || '-',
        Month: member.startMonth || '-',
        Year: member.startYear || '-',
        'Date of birth': member.dateOfBirth || '-',
        Status: member.status,
      }));

      // Descargar archivo (helper compartido lib/xlsx.ts: guard anti-fórmula + anchos)
      const fileName = `community_map_${new Date().toISOString().split('T')[0]}.xlsx`;
      await downloadXlsx(fileName, Object.keys(excelData[0] ?? {}), excelData.map((r) => Object.values(r)), {
        sheet: 'Community Map',
        colWidths: [8, 15, 15, 30, 15, 15, 30, 15, 20, 20, 20, 20, 20, 12, 8, 8, 15, 12],
      });

      toast.success(t('users.toasts.export_success', { fileName }));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error(t('users.toasts.export_error'));
    }
  };

  // Calculate stats from members data
  const stats = useMemo(() => {
    const uniqueCountries = new Set(members.map((m) => m.country));
    const uniqueClapbases = new Set(members.map((m) => m.clapbase));
    const activeMembers = members.filter((m) => m.status === 'Activo');

    return {
      totalMembers: members.length,
      activeMembers: activeMembers.length,
      countries: uniqueCountries.size,
      clapbases: uniqueClapbases.size,
    };
  }, [members]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('users.community_map_page.title')}</h2>
          <p className="text-muted-foreground">
            {t(
              stats.totalMembers === 1
                ? 'users.community_map_page.member_count'
                : 'users.community_map_page.member_count_plural',
              { count: stats.totalMembers }
            )}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => syncFromSheets.mutate()}
            disabled={syncFromSheets.isPending}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncFromSheets.isPending ? 'animate-spin' : ''}`} />
            {syncFromSheets.isPending ? t('users.community_map_page.sincronizando') : t('users.community_map_page.sincronizar_con_sheets')}
          </Button>
          <Button variant="outline" onClick={handleExportToExcel}>
            <FileDown className="mr-2 h-4 w-4" />
            {t('users.community_map_page.export_excel')}
          </Button>
          <Button onClick={handleCreate}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t('users.community_map_page.add_member')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('users.community_map_page.stats.total_members')}
              </p>
              <p className="text-2xl font-bold">{stats.totalMembers}</p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('users.community_map_page.stats.active')}
              </p>
              <p className="text-2xl font-bold">{stats.activeMembers}</p>
            </div>
            <Users className="h-8 w-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('users.community_map_page.stats.countries')}
              </p>
              <p className="text-2xl font-bold">{stats.countries}</p>
            </div>
            <Globe className="h-8 w-8 text-orange-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('users.community_map_page.stats.clapbases')}
              </p>
              <p className="text-2xl font-bold">{stats.clapbases}</p>
            </div>
            <Building className="h-8 w-8 text-purple-500" />
          </div>
        </Card>
      </div>

      {/* Community Map Table */}
      <Card>
        <CommunityMapTable
          members={members}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          filters={filters}
          onFiltersChange={setFilters}
        />
      </Card>

      {/* Create/Edit Dialog */}
      <CommunityMemberFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        mode={formMode}
        member={selectedMember}
        onSave={handleSave}
        allMembers={members}
      />
    </div>
  );
}
